import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService, StorageServiceKey, storageStore, StorageType } from '@app/services/storage';
import { environment } from '@env/environment';
import { DialogService } from '@services/dialog';
import { Endpoint, EndpointService } from '@services/endpoints/';
import { LanguageService } from '@services/language';
import { SystemAlertAction, SystemAlertId, SystemAlertsService } from '@services/system-alerts/';
import { TokenService } from '@services/token/';
import { IUser, UserService } from '@services/user/';
import { ObjectString } from '@shared/utilities';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProgressIndicatorService } from '../progress';
import { config } from '@env/config';
import { routePathName } from '@app/app.models';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private user: IUser;

	public get rememberMe(): string {
		return this.storageService.getItem(StorageType.localStorage, StorageServiceKey.rememberMe, storageStore);
	}

	public set rememberMe(rememberMe: string) {
		this.storageService.setItem(StorageType.localStorage, StorageServiceKey.rememberMe, rememberMe, storageStore);
	}

	public get returnUrl(): string {
		return this.storageService.getItem(StorageType.localStorage, StorageServiceKey.returnUrl, storageStore);
	}

	public set returnUrl(returnUrl: string) {
		this.storageService.setItem(StorageType.localStorage, StorageServiceKey.returnUrl, returnUrl, storageStore);
	}

	public get isAuthenticated(): boolean {
		return !this.tokenService.tokenExpired;
	}

	public set authUser(user: IUser) {
		this.user = user;

		if (user) {
			this.storeAuthUser(user);
		} else {
			this.deleteAuthUser();
		}
	}

	public get authUser(): IUser {
		const user = !this.user ? this.retrieveAuthUser() : this.user;

		if (!user) {
			return null;
		} else {
			this.user = user;
			this.tokenService.tokenEmail = this.user.email;
			return this.user;
		}
	}

	public get accountUser(): IUser {
		return this.authUser;
	}

	constructor(
		private http: HttpClient,
		private endpointService: EndpointService,
		private tokenService: TokenService,
		private systemAlertsService: SystemAlertsService,
		private userService: UserService,
		private storageService: StorageService,
		private languageService: LanguageService,
		private dialogService: DialogService,
		private router: Router,
		private progressIndicatorService: ProgressIndicatorService
	) {
		this.userService.dataObs.subscribe((users: ObjectString<IUser>) => {
			if (users[this.authUser?.id]) {
				this.authUser = users[this.authUser.id];
			}
		});
	}

	/**
	 * Get auth token, if this fails clear tokens and delete user since we assume this
	 * is not a valid session anymore.
	 */
	public getToken(): Observable<string> {
		return new Observable((observer) => {
			this.tokenService.getToken().subscribe(
				(token: string) => {
					observer.next(token);
					observer.complete();
				},
				(error: any) => {
					this.logout(this.router.routerState.snapshot.url);
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	/**
	 * Check if user is authenticated and refresh user details. If no valid token try
	 * to get a new one.
	 */
	public isAuthenticatedObserver(): Observable<boolean> {
		return new Observable<boolean>((observer) => {
			const getAuthUser = () => {
				this.getAuthUser().subscribe(
					(_) => {
						if (!activeSpinner) this.progressIndicatorService.spinner(false);
						observer.next(true);
						observer.complete();
					},
					() => {
						this.progressIndicatorService.spinner(false);
						observer.next(false);
						observer.complete();
					}
				);
			};

			const activeSpinner = this.progressIndicatorService.hasSpinner();

			if (!activeSpinner) this.progressIndicatorService.spinner(true);

			if (this.isAuthenticated) {
				getAuthUser();
			} else {
				this.tokenService.getToken().subscribe(
					() => getAuthUser(),
					() => {
						this.progressIndicatorService.spinner(false);
						observer.next(false);
						observer.complete();
					}
				);
			}
		});
	}

	/**
	 * Get auth user details from server. Will only refresh details of not available or if forced.
	 */
	public getAuthUser(force: boolean = false): Observable<IUser> {
		return new Observable<IUser>((observer) => {
			if (this.authUser && !force) {
				this.languageService.validateAppLanguage(this.authUser?.locale);
				observer.next(this.authUser);
				observer.complete();
			} else {
				this.userService.getUser().subscribe(
					(user: IUser) => {
						this.authUser = user;
						this.languageService.validateAppLanguage(user?.locale);

						observer.next(user);
						observer.complete();
					},
					(err: any) => {
						observer.error(err);
						observer.complete();
					}
				);
			}
		});
	}

	/**
	 * Calls auth login endpoint and stores returned tokens.
	 */
	public login(username: string, password: string): Observable<any> {
		return this.http
			.post<any>(`${this.endpointService.url(Endpoint.authLogin)}?response=quiet`, {
				username,
				password,
				clientId: config.clientId
			})
			.pipe(
				map((result: any) => {
					if (result) this.tokenService.extractTokens(username, config.clientId, result);
					return result;
				})
			);
	}

	/**
	 * Logout user by deleting user information and auth tokens. Will redirect user
	 * to login screen once done.
	 *
	 * @param returnUrl Optional return URL query param ensuring user gets redirect back on login
	 */
	public logout(returnUrl = ''): void {
		this.http.get(this.endpointService.url(Endpoint.authLogout)).subscribe({ error: () => {} });
		// don't need to wait for answer, just clear all tokens
		this.systemAlertsService.removeAll();
		this.authUser = null;
		this.tokenService.clearTokens();
		this.dialogService.closeAll(true);

		this.navigate(this.prepUrl(returnUrl));
	}

	private prepUrl(returnUrl = ''): string {
		if (returnUrl && !returnUrl.startsWith('http')) {
			returnUrl = `${environment.apps.portal}${returnUrl}`;
		}

		let url = `${environment.apps.portal}/${routePathName.signIn}`;

		if (returnUrl) {
			url += '?returnUrl=' + encodeURIComponent(returnUrl);
		}

		return url;
	}

	private navigate(url: string): void {
		window.location.assign(url);
	}

	/**
	 * Get auth user from client side storage.
	 */
	private retrieveAuthUser(): IUser {
		return this.storageService.getItem(StorageType.cookie, 'user', storageStore);
	}

	/**
	 * Store auth user in client side storage.
	 */
	private storeAuthUser(user: IUser): void {
		this.storageService.setItem(StorageType.cookie, 'user', user, storageStore);
	}

	/**
	 * Delete auth user from client side storage.
	 */
	private deleteAuthUser(): void {
		this.systemAlertsService.cancel(SystemAlertId.emailNotVerified, SystemAlertAction.cancelled, false);
		this.storageService.removeItem(StorageType.cookie, 'user', storageStore);
	}
}
